import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import {
  Typography, Grid, Paper, Box, Table, TableBody, TableCell, TableContainer, TableRow,
  Card, CardContent, Tooltip, IconButton, Button, TextField, Dialog, DialogTitle,
  DialogContent, DialogActions, FormControl, InputLabel, Select, MenuItem, FormHelperText, InputAdornment,
  Chip, Divider, Stack, useTheme, alpha, Container
} from '@mui/material';
import { usePermissions } from '../../../utils/permissions';
import { 
  HelpOutline, 
  Science as ScienceIcon,
  Assignment as AssignmentIcon,
  Settings as SettingsIcon,
  Info as InfoIcon,
  TrendingUp as TrendingUpIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import { supabase } from '../../../supabaseClient';
import { getAllUnits } from '../../../utils/unitConversions';

const OverviewTab = forwardRef(({ formulation, products, showSnackbar, isCreatingNew, userCompanyId, formulationIngredients = [], onUpdate, user, onBackToList }, ref) => {
  const theme = useTheme();
  const { canAccessPage, canEditPage, hasPermission, isManagerOrAbove, userRole, hierarchyLevel } = usePermissions();
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [localFormulation, setLocalFormulation] = useState(formulation);
  const [productSelectionDialogOpen, setProductSelectionDialogOpen] = useState(false);
  const [productSearchQuery, setProductSearchQuery] = useState('');
  const [formData, setFormData] = useState({
    name: formulation?.name || '',
    product_id: formulation?.product_id || '',
    description: formulation?.description || '',
    notes: formulation?.notes || '',
    ph_target: formulation?.ph_target || '',
    viscosity_target: formulation?.viscosity_target || '',
    appearance: formulation?.appearance || '',
    yield_percentage: formulation?.yield_percentage || '',
    target_batch_size: formulation?.target_batch_size || 100,
    unit_of_measure: formulation?.unit_of_measure || 'g',
    version: formulation?.version || '1.0',
    status: formulation?.status || 'draft',
    code: formulation?.code || '',
    type: formulation?.type || '',
  });

  // Expose methods to parent component
  useImperativeHandle(ref, () => ({
    openEditDialog: (isNew = false) => {
      if (isNew) {
        // For new formulations, use the default empty values
        setFormData({
          name: '',
          product_id: '',
          description: '',
          notes: '',
          ph_target: '',
          viscosity_target: '',
          appearance: '',
          yield_percentage: '',
          target_batch_size: 100,
          unit_of_measure: 'g',
          version: '1.0',
          status: 'draft',
          code: '',
          type: '',
        });
      } else {
        // For existing formulations, use the current values
        setFormData({
          name: formulation?.name || '',
          product_id: formulation?.product_id || '',
          description: formulation?.description || '',
          notes: formulation?.notes || '',
          ph_target: formulation?.ph_target || '',
          viscosity_target: formulation?.viscosity_target || '',
          appearance: formulation?.appearance || '',
          yield_percentage: formulation?.yield_percentage || '',
          target_batch_size: formulation?.target_batch_size || 100,
          unit_of_measure: formulation?.unit_of_measure || 'g',
          version: formulation?.version || '1.0',
          status: formulation?.status || 'draft',
          code: formulation?.code || '',
          type: formulation?.type || '',
        });
      }
      setEditDialogOpen(true);
    }
  }));

  // Update local state when formulation prop changes
  useEffect(() => {
    setLocalFormulation(formulation);
    if (formulation) {
      setFormData({
        name: formulation.name || '',
        code: formulation.code || '',
        description: formulation.description || '',
        product_id: formulation.product_id || '',
        target_batch_size: formulation.target_batch_size || 100,
        unit_of_measure: formulation.unit_of_measure || 'g',
        version: formulation.version || '1.0',
        status: formulation.status || 'draft',
        ph_target: formulation.ph_target || '',
        viscosity_target: formulation.viscosity_target || '',
        appearance: formulation.appearance || '',
        yield_percentage: formulation.yield_percentage || '',
        notes: formulation.notes || '',
        type: formulation.type || '',
      });

      // Auto-open dialog if formulation is incomplete
      const isIncomplete = !formulation.description || 
                          !formulation.type || 
                          !formulation.product_id ||
                          (!formulation.ph_target && !formulation.viscosity_target && !formulation.appearance);
      
      if (isIncomplete && !editDialogOpen) {
        setTimeout(() => setEditDialogOpen(true), 500);
      }
    }
  }, [formulation]);

  // Check permissions for overview tab specifically
  const canViewOverview = hasPermission('products.formulations.overview.view', 'read');
  const canEditOverview = hasPermission('products.formulations.overview.edit', 'write');
  const canDeleteOverview = hasPermission('products.formulations.overview.delete', 'delete');

  // Permission check
  if (!canViewOverview) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        justifyContent: 'center',
        minHeight: '60vh',
        textAlign: 'center',
        px: 3
      }}>
        <Card sx={{
          p: 6,
          borderRadius: 4,
          border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
          background: 'white',
          boxShadow: `0 12px 40px ${alpha(theme.palette.grey[900], 0.08)}`,
          maxWidth: 500,
          width: '100%',
          position: 'relative',
          overflow: 'hidden',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: `0 16px 48px ${alpha(theme.palette.primary.main, 0.15)}`,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
          },
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.03)}, transparent)`,
            zIndex: 0,
            opacity: 0.8,
            transition: 'opacity 0.3s ease'
          },
          '&:hover::before': {
            opacity: 1
          }
        }}>
          <Box sx={{ 
            p: 3, 
            borderRadius: 3, 
            background: `linear-gradient(${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
            mb: 6,
            display: 'inline-flex',
            boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.15)}`
          }}>
            <ScienceIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
          </Box>
          <Typography variant="h3" sx={{ 
            mb: 3, 
            fontWeight: 500, 
            color: 'text.primary',
            fontSize: '2.5rem',
            letterSpacing: '-0.8px'
          }}>
            Access Restricted
          </Typography>
          <Typography variant="h6" sx={{ 
            color: 'text.secondary', 
            mb: 4,
            fontWeight: 500,
            fontSize: '1.2rem'
          }}>
            Formulation Overview Required
          </Typography>
          <Typography variant="body1" sx={{ 
            color: 'text.secondary',
            lineHeight: 1.7,
            mb: 6,
            fontSize: '1.05rem',
            fontWeight: 500
          }}>
            You don't currently have the necessary permissions to access the Formulation Overview. Contact your system administrator to request access or verify your role permissions.
          </Typography>
        </Card>
      </Box>
    );
  }

  // Calculate the sum of all ingredient percentages
  const calculateTotalPercentage = () => {
    if (!formulationIngredients || formulationIngredients.length === 0) {
      return 0;
    }
    
    const total = formulationIngredients.reduce((sum, item) => {
      return sum + parseFloat(item.percentage || 0);
    }, 0);
    
    return total.toFixed(2);
  };

  // Calculate the completion percentage of the formulation
  const calculateCompletionPercentage = (formulation) => {
    if (!formulation) return 0;
    
    let total = 0;
    let completed = 0;
    
    total += 4;
    if (formulation.name) completed++;
    if (formulation.target_batch_size) completed++;
    if (formulation.unit_of_measure) completed++;
    if (formulation.description) completed++;
    
    total += 4;
    if (formulation.ph_target) completed++;
    if (formulation.viscosity_target) completed++;
    if (formulation.appearance) completed++;
    if (formulation.yield_percentage) completed++;
    
    total += 1;
    if (formulationIngredients?.length > 0) completed++;
    
    total += 1;
    if (formulation?.preparation_instructions) completed++;
    
    return Math.round((completed / total) * 100);
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'approved': return 'success';
      case 'in_review': return 'warning';
      case 'rejected': return 'error';
      default: return 'default';
    }
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSaveFormulation = async () => {
    setLoading(true);
    try {
      // Check required fields
      if (!formData.name) {
        showSnackbar('Formulation name is required', 'error');
        setLoading(false);
        return;
      }
      
      // Check if overview is complete
      const isOverviewComplete = !!(
        formData.name?.trim() && 
        formData.description?.trim() && 
        formData.target_batch_size && 
        formData.type?.trim()
      );
      
      // Prepare the data with proper numeric handling
      const formulationData = {
        name: formData.name.trim(),
        code: formData.code?.trim() || null,
        description: formData.description?.trim() || null,
        product_id: formData.product_id || null,
        target_batch_size: formData.target_batch_size ? parseFloat(formData.target_batch_size) : 100,
        unit_of_measure: formData.unit_of_measure || 'g',
        version: formData.version || '1.0',
        status: formData.status || 'draft',
        ph_target: formData.ph_target ? parseFloat(formData.ph_target) : null,
        viscosity_target: formData.viscosity_target?.trim() || null,
        appearance: formData.appearance?.trim() || null,
        yield_percentage: formData.yield_percentage ? parseFloat(formData.yield_percentage) : null,
        notes: formData.notes?.trim() || null,
        company_id: userCompanyId,
        updated_at: new Date().toISOString(),
        type: formData.type || '',
        overview_complete: isOverviewComplete,
        overview_completed_at: isOverviewComplete ? new Date().toISOString() : null,
        overview_completed_by: isOverviewComplete ? user?.id : null,
      };
      
      let result;
      
      if (isCreatingNew || !formulation?.id) {
        // Add created_at for new formulations
        formulationData.created_at = new Date().toISOString();
        
        const { data, error } = await supabase
          .from('formulations')
          .insert([formulationData])
          .select()
          .single();
        
        if (error) throw error;
        
        const newFormulation = data;
        
        setFormData({
          name: newFormulation.name || '',
          code: newFormulation.code || '',
          description: newFormulation.description || '',
          product_id: newFormulation.product_id || '',
          target_batch_size: newFormulation.target_batch_size || 100,
          unit_of_measure: newFormulation.unit_of_measure || 'g',
          version: newFormulation.version || '1.0',
          status: newFormulation.status || 'draft',
          ph_target: newFormulation.ph_target || '',
          viscosity_target: newFormulation.viscosity_target || '',
          appearance: newFormulation.appearance || '',
          yield_percentage: newFormulation.yield_percentage || '',
          notes: newFormulation.notes || '',
          type: newFormulation.type || '',
        });
        
        // Call onUpdate callback with the new formulation ID to trigger parent refresh
        if (onUpdate && typeof onUpdate === 'function') {
          onUpdate(newFormulation.id);
        }
      } else {
        // Update existing formulation
        const { data, error } = await supabase
          .from('formulations')
          .update(formulationData)
          .eq('id', formulation.id)
          .select()
          .single();
        
        if (error) throw error;
        
        const updatedFormulation = data;
        
        // Update local formulation state to reflect changes immediately
        setLocalFormulation(updatedFormulation);
        
        setFormData({
          name: updatedFormulation.name || '',
          code: updatedFormulation.code || '',
          description: updatedFormulation.description || '',
          product_id: updatedFormulation.product_id || '',
          target_batch_size: updatedFormulation.target_batch_size || 100,
          unit_of_measure: updatedFormulation.unit_of_measure || 'g',
          version: updatedFormulation.version || '1.0',
          status: updatedFormulation.status || 'draft',
          ph_target: updatedFormulation.ph_target || '',
          viscosity_target: updatedFormulation.viscosity_target || '',
          appearance: updatedFormulation.appearance || '',
          yield_percentage: updatedFormulation.yield_percentage || '',
          notes: updatedFormulation.notes || '',
          type: updatedFormulation.type || '',
        });
        
        // Call onUpdate callback to trigger parent refresh
        if (onUpdate && typeof onUpdate === 'function') {
          onUpdate();
        }
        
        showSnackbar('Formulation updated successfully!', 'success');
      }
      
      setEditDialogOpen(false);
    } catch (error) {
      console.error('Error saving formulation:', error);
      showSnackbar('Error saving formulation: ' + error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  if (!localFormulation) {
    return (
      <Box>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center',
          py: 8,
          textAlign: 'center'
        }}>
          <ScienceIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.disabled" gutterBottom>
            No formulation selected
          </Typography>
          <Typography variant="body2" color="text.disabled">
            Select a formulation to view its details
          </Typography>
        </Box>
      </Box>
    );
  }

  const product = products?.find(p => p.id === localFormulation.product_id);
  const totalPercentage = parseFloat(calculateTotalPercentage());

  return (
    <Box 
      sx={{ 
        pb: 4,
        display: 'flex',
        justifyContent: 'center',
        width: '100%'
      }}
    >
      <Box sx={{ maxWidth: '100vw', width: '100%', px: { xs: 2, sm: 3 } }}>
      {/* Header Section */}
      <Card 
        sx={{ 
          borderRadius: 3,
          mb: 2,
          border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
          background: 'white',
          boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: `0 20px 40px ${alpha(theme.palette.primary.main, 0.25)}`,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
          }
        }}
      >
        <CardContent sx={{ p: 2.5 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h5" sx={{ fontWeight: 500, mb: 1, color: theme.palette.text.primary }}>
                {localFormulation.name}
              </Typography>
              <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                <Chip 
                  label={localFormulation.status?.charAt(0).toUpperCase() + localFormulation.status?.slice(1)}
                  color={getStatusColor(localFormulation.status)}
                  size="small"
                  sx={{ fontWeight: 500 }}
                />
                {localFormulation.code && (
                  <Chip 
                    label={`Code: ${localFormulation.code}`}
                    variant="outlined"
                    size="small"
                    sx={{ 
                      borderColor: theme.palette.primary.main, 
                      color: theme.palette.primary.main,
                      fontWeight: 500
                    }}
                  />
                )}
                <Chip 
                  label={`v${localFormulation.version}`}
                  variant="outlined"
                  size="small"
                  sx={{ 
                    borderColor: theme.palette.primary.main, 
                    color: theme.palette.primary.main,
                    fontWeight: 500
                  }}
                />
              </Stack>
              {localFormulation.description && (
                <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 600 }}>
                  {localFormulation.description}
                </Typography>
              )}
            </Box>
            {canEditOverview && (
              <Button 
                variant="contained" 
                onClick={() => setEditDialogOpen(true)}
                sx={{ 
                  borderRadius: 2,
                  px: 3,
                  py: 1.5,
                  background: theme.palette.primary.main,
                  color: 'white',
                  fontWeight: 500,
                boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  background: theme.palette.primary.dark,
                  transform: 'translateY(-2px)',
                  boxShadow: `0 6px 16px ${alpha(theme.palette.primary.main, 0.4)}`
                }
              }}
            >
              Edit Details
            </Button>
            )}
          </Box>
        </CardContent>
      </Card>
      
      <Grid container spacing={2} sx={{ mb: 2 }}>
        {/* Basic Information */}
        <Grid item xs={12} md={6}>
          <Card 
            sx={{ 
              height: '100%',
              borderRadius: 3,
              border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
              background: 'white',
              boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: `0 20px 40px ${alpha(theme.palette.primary.main, 0.25)}`,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
              }
            }}
          >
            <Box sx={{ 
              p: 1.5, 
              borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
              background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`
            }}>
              <Stack direction="row" spacing={2} alignItems="center">
                <Box sx={{ 
                  p: 1, 
                  borderRadius: 2, 
                  background: alpha(theme.palette.primary.main, 0.1),
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
                }}>
                  <AssignmentIcon fontSize="small" sx={{ color: theme.palette.primary.main }} />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
                  General Information
                </Typography>
              </Stack>
            </Box>
            
            <Box sx={{ p: 2 }}>
              <Stack spacing={2}>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                    PRODUCT
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500, mt: 0.5 }}>
                    {product?.name || 'Not assigned'}
                  </Typography>
                </Box>
                
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                    TYPE
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500, mt: 0.5 }}>
                    {localFormulation.type ? localFormulation.type.charAt(0).toUpperCase() + localFormulation.type.slice(1) : 'Unspecified'}
                  </Typography>
                </Box>
                
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                    CODE
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500, mt: 0.5 }}>
                    {localFormulation.code || '—'}
                  </Typography>
                </Box>
                
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                    VERSION
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500, mt: 0.5 }}>
                    {localFormulation.version || '1.0'}
                  </Typography>
                </Box>
                
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                    BATCH SIZE
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500, mt: 0.5 }}>
                    {localFormulation.target_batch_size} {localFormulation.unit_of_measure}
                  </Typography>
                </Box>
                
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                    DATE CREATED
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500, mt: 0.5 }}>
                    {localFormulation.created_at ? new Date(localFormulation.created_at).toLocaleDateString() : '—'}
                  </Typography>
                </Box>
                
                {localFormulation.description && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                      DESCRIPTION
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>
                      {localFormulation.description}
                    </Typography>
                  </Box>
                )}
                
                {localFormulation.notes && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                      NOTES
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>
                      {localFormulation.notes}
                    </Typography>
                  </Box>
                )}
              </Stack>
            </Box>
          </Card>
        </Grid>

        {/* Technical Specifications */}
        <Grid item xs={12} md={6}>
          <Card 
            sx={{ 
              height: '100%',
              borderRadius: 3,
              border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
              background: 'white',
              boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: `0 20px 40px ${alpha(theme.palette.primary.main, 0.25)}`,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
              }
            }}
          >
            <Box sx={{ 
              p: 1.5, 
              borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
              background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`
            }}>
              <Stack direction="row" spacing={2} alignItems="center">
                <Box sx={{ 
                  p: 1, 
                  borderRadius: 2, 
                  background: alpha(theme.palette.primary.main, 0.1),
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
                }}>
                  <SettingsIcon fontSize="small" sx={{ color: theme.palette.primary.main }} />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
                  Technical Specifications
                </Typography>
              </Stack>
            </Box>
            
            <Box sx={{ p: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                    pH TARGET
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 500, mt: 0.5 }}>
                    {localFormulation.ph_target || '—'}
                  </Typography>
                </Grid>
                
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                    VISCOSITY TARGET
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 500, mt: 0.5 }}>
                    {localFormulation.viscosity_target || '—'}
                  </Typography>
                </Grid>
                
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                    APPEARANCE
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 500, mt: 0.5 }}>
                    {localFormulation.appearance || '—'}
                  </Typography>
                </Grid>
                
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                    YIELD
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 500, mt: 0.5 }}>
                    {localFormulation.yield_percentage ? `${localFormulation.yield_percentage}%` : '—'}
                  </Typography>
                </Grid>
                
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                      TOTAL PERCENTAGE
                    </Typography>
                    <Tooltip title="The sum of all ingredient percentages. Should equal 100% in a complete formulation.">
                      <IconButton size="small" sx={{ p: 0.5 }}>
                        <InfoIcon fontSize="small" color="action" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                    <Typography 
                      variant="h5" 
                      sx={{ 
                        fontWeight: 500,
                        color: totalPercentage === 100 ? 'success.main' : 
                               totalPercentage > 100 ? 'error.main' : 
                               'warning.main'
                      }}
                    >
                      {totalPercentage}%
                    </Typography>
                    {totalPercentage === 100 && (
                      <Chip 
                        label="Complete" 
                        color="success" 
                        size="small" 
                        sx={{ ml: 2, fontWeight: 500 }} 
                      />
                    )}
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Card>
        </Grid>
      </Grid>

      {/* Product Selection Dialog */}
      <Dialog
        open={productSelectionDialogOpen}
        onClose={() => {
          setProductSelectionDialogOpen(false);
          setProductSearchQuery('');
        }}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: 3,
            border: 'none',
            background: `linear-gradient(135deg, ${alpha(theme.palette.grey[50], 0.9)}, ${alpha(theme.palette.grey[100], 0.9)})`,
            backdropFilter: 'blur(20px)',
            boxShadow: `0 24px 48px ${alpha(theme.palette.primary.main, 0.15)}`,
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle sx={{ 
          p: 4, 
          borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.04)})`,
          color: theme.palette.text.primary,
          fontWeight: 500
        }}>
          Select Product
        </DialogTitle>
        <DialogContent sx={{ p: 4 }}>
          <Box sx={{ mb: 4, mt: 3 }}>
            <TextField
              fullWidth
              placeholder="Search products..."
              value={productSearchQuery}
              onChange={(e) => setProductSearchQuery(e.target.value)}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  backdropFilter: 'blur(10px)',
                  '& fieldset': {
                    borderColor: alpha(theme.palette.primary.main, 0.3),
                    borderWidth: 1
                  },
                  '&:hover fieldset': {
                    borderColor: theme.palette.primary.main
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: theme.palette.primary.main
                  }
                }
              }}
            />
          </Box>
          
          <Box sx={{ 
            maxHeight: 400, 
            overflowY: 'auto',
            border: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
            borderRadius: 2,
            background: 'white',
            '&::-webkit-scrollbar': {
              display: 'none'
            },
            scrollbarWidth: 'none'
          }}>
            {/* Clear Selection Option - only show when not searching */}
            {!productSearchQuery && (
              <Box
                onClick={() => {
                  setFormData(prev => ({
                    ...prev,
                    product_id: ''
                  }));
                  setProductSelectionDialogOpen(false);
                  setProductSearchQuery('');
                }}
                sx={{
                  p: 3,
                  borderBottom: `1px solid ${alpha(theme.palette.grey[200], 0.8)}`,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  backgroundColor: alpha(theme.palette.grey[100], 0.5),
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.grey[100], 0.8),
                    transform: 'translateX(4px)',
                    boxShadow: 'inset 4px 0 0 black'
                  }
                }}
              >
                <Typography variant="subtitle1" sx={{ mb: 0.5, fontStyle: 'italic' }}>
                  No Product (Clear Selection)
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Remove product assignment from this formulation
                </Typography>
              </Box>
            )}
            
            {products
              ?.filter(product => 
                product.name.toLowerCase().includes(productSearchQuery.toLowerCase()) ||
                (product.description && product.description.toLowerCase().includes(productSearchQuery.toLowerCase()))
              )
              .map(product => (
                <Box
                  key={product.id}
                  onClick={() => {
                    setFormData(prev => ({
                      ...prev,
                      product_id: product.id
                    }));
                    setProductSelectionDialogOpen(false);
                    setProductSearchQuery('');
                  }}
                  sx={{
                    p: 3,
                    borderBottom: `1px solid ${alpha(theme.palette.grey[200], 0.8)}`,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.primary.main, 0.04),
                      transform: 'translateX(4px)',
                      boxShadow: 'inset 4px 0 0 black'
                    },
                    '&:last-child': {
                      borderBottom: 'none'
                    }
                  }}
                >
                  <Typography variant="subtitle1">
                    {product.name}
                  </Typography>
                </Box>
              ))
            }
            {(!products || products.length === 0) && (
              <Box sx={{ p: 6, textAlign: 'center' }}>
                <Typography variant="body1" color="text.secondary">
                  No products found.
                </Typography>
              </Box>
            )}
            {products && products
              ?.filter(product => 
                product.name.toLowerCase().includes(productSearchQuery.toLowerCase()) ||
                (product.description && product.description.toLowerCase().includes(productSearchQuery.toLowerCase()))
              ).length === 0 && productSearchQuery && (
              <Box sx={{ p: 6, textAlign: 'center' }}>
                <Typography variant="body1" color="text.secondary">
                  No products found matching your search.
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ 
          p: 4, 
          gap: 2, 
          borderTop: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.04)})`
        }}>
          <Button 
            onClick={() => {
              if (userCompanyId) {
                window.open(`/company/${userCompanyId}/products`, '_blank');
              } else {
                window.open(`/products`, '_blank');
              }
            }}
            variant="outlined"
            sx={{ 
              borderRadius: 2,
              px: 3,
              py: 1.5,
              borderColor: alpha(theme.palette.primary.main, 0.3),
              color: theme.palette.primary.main,
              fontWeight: 500,
              background: 'white',
              '&:hover': {
                borderColor: theme.palette.primary.main,
                backgroundColor: alpha(theme.palette.primary.main, 0.04)
              }
            }}
          >
            + Add New Product
          </Button>
          <Button 
            onClick={() => {
              setProductSelectionDialogOpen(false);
              setProductSearchQuery('');
            }}
            sx={{ 
              borderRadius: 2,
              px: 3,
              py: 1.5,
              color: theme.palette.text.secondary,
              '&:hover': {
                bgcolor: alpha(theme.palette.text.primary, 0.04)
              }
            }}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog - moved here so it's always available */}
      <Dialog 
        open={editDialogOpen} 
        onClose={isCreatingNew ? undefined : () => setEditDialogOpen(false)} 
        fullWidth 
        maxWidth="md"
        PaperProps={{
          sx: { 
            borderRadius: 3,
            border: 'none',
            background: `linear-gradient(135deg, ${alpha(theme.palette.grey[50], 0.9)}, ${alpha(theme.palette.grey[100], 0.9)})`,
            backdropFilter: 'blur(20px)',
            boxShadow: `0 24px 48px ${alpha(theme.palette.primary.main, 0.15)}`,
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle sx={{ 
          p: 4, 
          borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.04)})`,
          color: theme.palette.text.primary,
          fontWeight: 500
        }}>
          {isCreatingNew ? 'Create New Formulation' : 'Edit Formulation'}
        </DialogTitle>
        <DialogContent sx={{ p: 4 }}>
          <Grid container spacing={3} sx={{ mt: 0.5 }}>
            {/* Basic Information */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 500, mb: 2 }}>
                Basic Information
              </Typography>
              
              <TextField
                name="name"
                label="Name"
                fullWidth
                value={formData.name}
                onChange={handleFormChange}
                required
                margin="normal"
                error={!formData.name}
                helperText={!formData.name ? "Name is required" : "Enter a descriptive name for your formulation"}
                sx={{ 
                  '& .MuiOutlinedInput-root': { 
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    backdropFilter: 'blur(10px)',
                    '& fieldset': {
                      borderColor: alpha(theme.palette.primary.main, 0.3),
                      borderWidth: 1
                    },
                    '&:hover fieldset': {
                      borderColor: theme.palette.primary.main
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: theme.palette.primary.main
                    }
                  }
                }}
              />
              
              <TextField
                name="code"
                label="Code"
                fullWidth
                value={formData.code}
                onChange={handleFormChange}
                margin="normal"
                helperText="Optional internal code for this formulation"
                sx={{ 
                  '& .MuiOutlinedInput-root': { 
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    backdropFilter: 'blur(10px)',
                    '& fieldset': {
                      borderColor: alpha(theme.palette.primary.main, 0.3),
                      borderWidth: 1
                    },
                    '&:hover fieldset': {
                      borderColor: theme.palette.primary.main
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: theme.palette.primary.main
                    }
                  }
                }}
              />
              
              <Button
                fullWidth
                variant="outlined"
                onClick={() => setProductSelectionDialogOpen(true)}
                sx={{
                  borderRadius: 2,
                  py: 2,
                  mt: 2,
                  mb: 1,
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  backdropFilter: 'blur(10px)',
                  borderColor: alpha(theme.palette.primary.main, 0.3),
                  color: formData.product_id ? theme.palette.text.primary : theme.palette.text.secondary,
                  justifyContent: 'flex-start',
                  textAlign: 'left',
                  '&:hover': {
                    borderColor: theme.palette.primary.main,
                    backgroundColor: alpha(theme.palette.primary.main, 0.04)
                  }
                }}
              >
                {formData.product_id 
                  ? products?.find(p => p.id === formData.product_id)?.name || 'Select Product'
                  : 'Select Product'
                }
              </Button>
            
              <Grid container spacing={2} sx={{ mt: 0.5 }}>
                <Grid item xs={8}>
                  <TextField
                    name="target_batch_size"
                    label="Target Batch Size"
                    type="number"
                    InputProps={{
                      inputProps: { min: 0, step: 0.01 }
                    }}
                    value={formData.target_batch_size}
                    onChange={handleFormChange}
                    required
                    fullWidth
                    margin="normal"
                    variant="outlined"
                    helperText="The intended size of production batches"
                    sx={{ 
                      '& .MuiOutlinedInput-root': { 
                        borderRadius: 2,
                        backgroundColor: 'rgba(255, 255, 255, 0.9)',
                        backdropFilter: 'blur(10px)',
                        '& fieldset': {
                          borderColor: alpha(theme.palette.primary.main, 0.3),
                          borderWidth: 1
                        },
                        '&:hover fieldset': {
                          borderColor: theme.palette.primary.main
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: theme.palette.primary.main
                        }
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={4}>
                  <FormControl 
                    fullWidth 
                    margin="normal"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        backgroundColor: 'rgba(255, 255, 255, 0.9)',
                        backdropFilter: 'blur(10px)',
                        '& fieldset': {
                          borderColor: alpha(theme.palette.primary.main, 0.3),
                          borderWidth: 1
                        },
                        '&:hover fieldset': {
                          borderColor: theme.palette.primary.main
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: theme.palette.primary.main
                        }
                      }
                    }}
                  >
                    <InputLabel>Unit</InputLabel>
                    <Select
                      name="unit_of_measure"
                      value={formData.unit_of_measure}
                      onChange={handleFormChange}
                      label="Unit"
                    >
                      {getAllUnits().map(unit => (
                        <MenuItem key={unit} value={unit}>
                          {unit}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Grid>
            
            {/* Additional Information */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 500, mb: 2 }}>
                Additional Information
              </Typography>
              
              <FormControl 
                fullWidth 
                margin="normal"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    backdropFilter: 'blur(10px)',
                    '& fieldset': {
                      borderColor: alpha(theme.palette.primary.main, 0.3),
                      borderWidth: 1
                    },
                    '&:hover fieldset': {
                      borderColor: theme.palette.primary.main
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: theme.palette.primary.main
                    }
                  }
                }}
              >
                <InputLabel>Status</InputLabel>
                {formData.status === 'approved' ? (
                  <Select
                    name="status"
                    value="approved"
                    label="Status"
                    disabled
                    sx={{
                      '& .MuiSelect-select': {
                        color: theme.palette.success.main,
                        fontWeight: 500
                      },
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        backgroundColor: alpha(theme.palette.success.main, 0.1),
                        '& fieldset': {
                          borderColor: theme.palette.success.main,
                          borderWidth: 1
                        }
                      }
                    }}
                  >
                    <MenuItem value="approved">Approved</MenuItem>
                  </Select>
                ) : (
                  <Select
                    name="status"
                    value={formData.status}
                    onChange={handleFormChange}
                    label="Status"
                  >
                    <MenuItem value="draft">Draft</MenuItem>
                    <MenuItem value="in_review">In Review</MenuItem>
                    <MenuItem value="rejected">Rejected</MenuItem>
                  </Select>
                )}
                <FormHelperText>
                  {formData.status === 'approved' 
                    ? 'This formulation has been approved and cannot be edited' 
                    : 'Current status in the development process'
                  }
                </FormHelperText>
              </FormControl>
              
              <FormControl 
                fullWidth 
                margin="normal"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    backdropFilter: 'blur(10px)',
                    '& fieldset': {
                      borderColor: alpha(theme.palette.primary.main, 0.3),
                      borderWidth: 1
                    },
                    '&:hover fieldset': {
                      borderColor: theme.palette.primary.main
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: theme.palette.primary.main
                    }
                  }
                }}
              >
                <InputLabel>Type</InputLabel>
                <Select
                  name="type"
                  value={formData.type}
                  onChange={handleFormChange}
                  label="Type"
                >
                  <MenuItem value="">
                    <em>Select Type</em>
                  </MenuItem>
                  <MenuItem value="cream">Cream</MenuItem>
                  <MenuItem value="serum">Serum</MenuItem>
                  <MenuItem value="lotion">Lotion</MenuItem>
                  <MenuItem value="oil">Oil</MenuItem>
                  <MenuItem value="balm">Balm</MenuItem>
                  <MenuItem value="cleanser">Cleanser</MenuItem>
                  <MenuItem value="toner">Toner</MenuItem>
                  <MenuItem value="mask">Mask</MenuItem>
                  <MenuItem value="exfoliant">Exfoliant</MenuItem>
                  <MenuItem value="moisturizer">Moisturizer</MenuItem>
                </Select>
                <FormHelperText>Type or category of this formulation</FormHelperText>
              </FormControl>
              
              <TextField
                name="version"
                label="Version"
                fullWidth
                value={formData.version}
                onChange={handleFormChange}
                margin="normal"
                helperText="Version number of this formulation"
                sx={{ 
                  '& .MuiOutlinedInput-root': { 
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    backdropFilter: 'blur(10px)',
                    '& fieldset': {
                      borderColor: alpha(theme.palette.primary.main, 0.3),
                      borderWidth: 1
                    },
                    '&:hover fieldset': {
                      borderColor: theme.palette.primary.main
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: theme.palette.primary.main
                    }
                  }
                }}
              />
              
              <TextField
                name="description"
                label="Description"
                fullWidth
                multiline
                rows={2}
                value={formData.description}
                onChange={handleFormChange}
                margin="normal"
                helperText="Add a brief description of this formulation"
                sx={{ 
                  '& .MuiOutlinedInput-root': { 
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    backdropFilter: 'blur(10px)',
                    '& fieldset': {
                      borderColor: alpha(theme.palette.primary.main, 0.3),
                      borderWidth: 1
                    },
                    '&:hover fieldset': {
                      borderColor: theme.palette.primary.main
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: theme.palette.primary.main
                    }
                  }
                }}
              />
            </Grid>
            
            {/* Technical Specifications */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 500, mt: 2, mb: 2 }}>
                Technical Specifications
              </Typography>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                name="ph_target"
                label="pH Target"
                fullWidth
                value={formData.ph_target}
                onChange={handleFormChange}
                type="number"
                inputProps={{ step: '0.1' }}
                margin="normal"
                sx={{ 
                  '& .MuiOutlinedInput-root': { 
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    backdropFilter: 'blur(10px)',
                    '& fieldset': {
                      borderColor: alpha(theme.palette.primary.main, 0.3),
                      borderWidth: 1
                    },
                    '&:hover fieldset': {
                      borderColor: theme.palette.primary.main
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: theme.palette.primary.main
                    }
                  }
                }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                name="viscosity_target"
                label="Viscosity Target"
                fullWidth
                value={formData.viscosity_target}
                onChange={handleFormChange}
                margin="normal"
                sx={{ 
                  '& .MuiOutlinedInput-root': { 
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    backdropFilter: 'blur(10px)',
                    '& fieldset': {
                      borderColor: alpha(theme.palette.primary.main, 0.3),
                      borderWidth: 1
                    },
                    '&:hover fieldset': {
                      borderColor: theme.palette.primary.main
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: theme.palette.primary.main
                    }
                  }
                }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                name="appearance"
                label="Appearance"
                fullWidth
                value={formData.appearance}
                onChange={handleFormChange}
                margin="normal"
                sx={{ 
                  '& .MuiOutlinedInput-root': { 
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    backdropFilter: 'blur(10px)',
                    '& fieldset': {
                      borderColor: alpha(theme.palette.primary.main, 0.3),
                      borderWidth: 1
                    },
                    '&:hover fieldset': {
                      borderColor: theme.palette.primary.main
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: theme.palette.primary.main
                    }
                  }
                }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                name="yield_percentage"
                label="Expected Yield (%)"
                fullWidth
                value={formData.yield_percentage}
                onChange={handleFormChange}
                type="number"
                InputProps={{
                  inputProps: { min: 0, max: 100, step: 0.1 },
                  endAdornment: <InputAdornment position="end">%</InputAdornment>,
                }}
                margin="normal"
                sx={{ 
                  '& .MuiOutlinedInput-root': { 
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    backdropFilter: 'blur(10px)',
                    '& fieldset': {
                      borderColor: alpha(theme.palette.primary.main, 0.3),
                      borderWidth: 1
                    },
                    '&:hover fieldset': {
                      borderColor: theme.palette.primary.main
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: theme.palette.primary.main
                    }
                  }
                }}
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                name="notes"
                label="Notes"
                fullWidth
                multiline
                rows={3}
                value={formData.notes}
                onChange={handleFormChange}
                margin="normal"
                helperText="Any additional notes or observations about this formulation"
                sx={{ 
                  '& .MuiOutlinedInput-root': { 
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    backdropFilter: 'blur(10px)',
                    '& fieldset': {
                      borderColor: alpha(theme.palette.primary.main, 0.3),
                      borderWidth: 1
                    },
                    '&:hover fieldset': {
                      borderColor: theme.palette.primary.main
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: theme.palette.primary.main
                    }
                  }
                }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ 
          p: 4, 
          gap: 2, 
          borderTop: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.04)})`
        }}>
          {!isCreatingNew && (
            <Button 
              onClick={() => setEditDialogOpen(false)}
              sx={{ 
                borderRadius: 2,
                px: 3,
                py: 1.5,
                color: theme.palette.text.secondary,
                fontWeight: 500,
                background: 'rgba(255, 255, 255, 0.8)',
                backdropFilter: 'blur(10px)',
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.04)
                }
              }}
            >
              Cancel
            </Button>
          )}
          {isCreatingNew && (
            <Button 
              onClick={onBackToList}
              sx={{ 
                borderRadius: 2,
                px: 3,
                py: 1.5,
                color: theme.palette.text.secondary,
                fontWeight: 500,
                background: 'rgba(255, 255, 255, 0.8)',
                backdropFilter: 'blur(10px)',
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.04)
                }
              }}
            >
              Cancel
            </Button>
          )}
          {canEditOverview && (
            <Button 
              onClick={handleSaveFormulation} 
              variant="contained" 
              disabled={loading || !formData.name || !formData.code || !formData.product_id || !formData.target_batch_size || !formData.unit_of_measure || !formData.version || !formData.description || !formData.status || !formData.type || !formData.notes || !formData.ph_target || !formData.viscosity_target || !formData.appearance || !formData.yield_percentage}
              sx={{ 
                borderRadius: 2,
                px: 4,
                py: 1.5,
                background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                color: 'white',
                fontWeight: 500,
                boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
                '&:hover': {
                  background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                  boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.4)}`
                },
                '&:disabled': {
                  background: theme.palette.grey[400],
                  color: theme.palette.grey[100]
                }
              }}
            >
              {loading ? 'Saving...' : (isCreatingNew ? 'Create Formulation' : 'Save Changes')}
            </Button>
          )}
        </DialogActions>
      </Dialog>
      </Box>
    </Box>
  );
});

export default OverviewTab;